import type { PolicySection } from '../types/policy'

export const termsMeta = {
  company: 'Fem Entity Inc.',
  title: 'Practitioner Terms of Service',
  effectiveDate: 'August 2026',
  summary:
    'The agreement between Fem Entity and the practitioners who offer their services to clients through the Fem Entity Platform.',
  pdfUrl:
    'https://cdn.magicpatterns.com/uploads/wfZ1pfrMxbyeGn2hM5VihE/Practitioner_TOS_Aug2026_pdf.pdf',
  email: 'hello@fementity.ca',
  preamble:
    'These Practitioner Terms of Service (“Terms”) constitute a binding agreement between the Practitioner (“you” or “Practitioner”) and Fem Entity Inc., a corporation incorporated under the Canada Business Corporations Act, with its registered office in Nova Scotia, Canada (“Fem Entity,” “we,” “us,” or “our”). By creating an account and accessing the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.',
}

export const termsSections: PolicySection[] = [
  {
    id: 'definitions',
    number: 1,
    title: 'Definitions',
    blocks: [
      {
        kind: 'clause',
        number: '1.1',
        text: 'In these Terms, unless the context otherwise requires, the following terms shall have the meanings ascribed to them below:',
      },
      {
        kind: 'clause',
        number: '1.2',
        text: '“Platform” means the Fem Entity mobile application and the Fem Entity website, and all related services, tools, and technologies provided by Fem Entity to facilitate the connection between Practitioners and Clients.',
      },
      {
        kind: 'clause',
        number: '1.3',
        text: '“Practitioner” means an individual who has been accepted by Fem Entity to offer professional services to Clients through the Platform.',
      },
      {
        kind: 'clause',
        number: '1.4',
        text: '“Client” means an individual who uses the Platform to find, engage, and book appointments with Practitioners.',
      },
      {
        kind: 'clause',
        number: '1.5',
        text: '“Personal Information” has the meaning ascribed to it in the Personal Information Protection and Electronic Documents Act, S.C. 2000, c. 5 (“PIPEDA”).',
      },
      {
        kind: 'clause',
        number: '1.6',
        text: '“Personal Health Information” has the meaning ascribed to it in the Personal Health Information Protection Act, 2004, S.O. 2004, c. 3, Sched. A (“PHIPA”) (Ontario).',
      },
      {
        kind: 'clause',
        number: '1.7',
        text: '“Services” means the professional services offered by the Practitioner to Clients, whether delivered in person or virtually.',
      },
      {
        kind: 'clause',
        number: '1.8',
        text: '“Fem Entity” or “we” means Fem Entity Inc., a corporation incorporated under the Canada Business Corporations Act.',
      },
    ],
  },
  {
    id: 'eligibility',
    number: 2,
    title: 'Eligibility',
    blocks: [
      {
        kind: 'sub',
        number: '2.1',
        title: 'Regulated Practitioners',
      },
      {
        kind: 'clause',
        number: '2.1.1',
        text: 'Practitioners who are members of a regulated health profession must maintain active registration and practising status with the applicable regulatory authority in every province or territory in which they offer Services; provide verification information or supporting documentation reasonably requested by Fem Entity; and promptly notify Fem Entity of any restriction, suspension, expiry, revocation, resignation, or other material change to that status.',
      },
      {
        kind: 'sub',
        number: '2.2',
        title: 'Association-Credentialed or Certified Practitioners',
      },
      {
        kind: 'clause',
        number: '2.2.1',
        text: 'Practitioners who are not provincially regulated must hold an active credential from an association or certifying body accepted by Fem Entity, describe their credential and scope accurately, provide supporting documentation reasonably requested by Fem Entity, and not imply that they are provincially regulated unless they separately hold an applicable regulated credential.',
      },
      {
        kind: 'sub',
        number: '2.3',
        title: 'All Practitioners',
      },
      {
        kind: 'clause',
        number: '2.3.1',
        text: 'All Practitioners must be at least eighteen (18) years of age, be legally authorized to provide their Services in Canada, comply with the Practitioner Verification Policy, and maintain all credentials, registrations, certifications, insurance, or equivalent professional protection required by applicable law, their regulator or professional association, and their applicable practice setting.',
      },
    ],
  },
  {
    id: 'account-and-access',
    number: 3,
    title: 'Account and Access',
    blocks: [
      {
        kind: 'clause',
        number: '3.1',
        text: 'Practitioners must create an account using accurate and complete information, keep their login credentials secure and confidential, and remain responsible for activity under their account.',
      },
      {
        kind: 'clause',
        number: '3.2',
        text: 'Practitioners must maintain an accurate and current profile, including their credentials, authorized provinces, availability, areas of practice, and contact information.',
      },
      {
        kind: 'clause',
        number: '3.3',
        text: 'Fem Entity may verify identity, credentials, certifications, registrations, insurance or professional protection, and other eligibility representations using public registers, information supplied by the Practitioner, an applicable authority or association, or a qualified third-party verification provider.',
      },
      {
        kind: 'clause',
        number: '3.4',
        text: 'Fem Entity may request supporting documentation and may restrict, suspend, or delay activation of an account while completing an initial, periodic, or risk-based verification review.',
      },
    ],
  },
  {
    id: 'relationship-of-the-parties',
    number: 4,
    title: 'Relationship of the Parties',
    blocks: [
      {
        kind: 'clause',
        number: '4.1',
        text: 'The Practitioner is an independent contractor and is not an employee, agent, partner, or joint venturer of Fem Entity. Nothing in these Terms shall be construed to create an employment relationship, partnership, joint venture, or agency relationship between the parties.',
      },
      {
        kind: 'clause',
        number: '4.2',
        text: 'The Practitioner is solely responsible for the payment of all applicable taxes, remittances, employment insurance premiums, Canada Pension Plan contributions, benefits, and insurance arising from or related to the Practitioner’s Services.',
      },
      {
        kind: 'clause',
        number: '4.3',
        text: 'Fem Entity does not supervise, direct, or control how Practitioners deliver their Services to Clients. The manner, method, and means of delivering Services are within the sole discretion of the Practitioner.',
      },
      {
        kind: 'clause',
        number: '4.4',
        text: 'The Practitioner sets their own availability and retains the right to decline any Client or appointment at their sole discretion, subject to professional and ethical obligations.',
      },
    ],
  },
  {
    id: 'platform-services',
    number: 5,
    title: 'Platform Services',
    blocks: [
      {
        kind: 'sub',
        number: '5.1',
        title: 'Services Provided by Fem Entity',
      },
      {
        kind: 'clause',
        number: '5.1.1',
        text: 'Fem Entity provides the following services through the Platform:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Discovery, matching, navigation, and referral functionality based on Client preferences, information, requests, and Practitioner availability;',
          },
          {
            text: 'Scheduling and booking tools to facilitate appointment administration;',
          },
          {
            text: 'Client information sharing with Practitioners or clinics where the Client deliberately selects the Practitioner or requests a referral and any required consent has been obtained;',
          },
          {
            text: 'Payment-processing functionality through Fem Entity’s payment service provider;',
          },
          {
            text: 'Storage and display of practitioner-provided meeting links and basic scheduling metadata; and',
          },
          {
            text: 'A mechanism allowing a Client to send one optional pre-appointment message to a selected Practitioner or clinic.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '5.2',
        title: 'Services Not Provided by Fem Entity',
      },
      {
        kind: 'clause',
        number: '5.2.1',
        text: 'For greater certainty, Fem Entity does not provide:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Clinical oversight, supervision, or direction of Practitioner Services;',
          },
          {
            text: 'Medical advice, diagnosis, treatment, or professional healthcare services;',
          },
          {
            text: 'A video-conferencing or virtual-care platform; or',
          },
          {
            text: 'A clinical-records or electronic medical-record system for Practitioners.',
          },
        ],
      },
      {
        kind: 'clause',
        number: '5.2.2',
        text: 'Any matching, navigation, or referral functionality supports discovery and access. It does not replace the Practitioner’s independent professional assessment, intake, consent process, or clinical judgment.',
      },
    ],
  },
  {
    id: 'data-responsibilities',
    number: 6,
    title: 'Data Responsibilities',
    blocks: [
      {
        kind: 'clause',
        number: '6.1',
        text: 'Fem Entity is accountable under applicable privacy legislation for Personal Information under its control. Fem Entity does not assume responsibility for clinical or professional records independently created or maintained by a Practitioner or clinic.',
      },
      {
        kind: 'clause',
        number: '6.2',
        text: 'The Practitioner is independently responsible for the privacy, confidentiality, security, retention, and lawful handling of Client information within any recordkeeping, practice-management, scheduling, virtual-care, or other professional system the Practitioner selects or controls.',
      },
      {
        kind: 'clause',
        number: '6.3',
        text: 'The Practitioner must establish and maintain privacy and records-management practices that comply with the laws, professional standards, and contractual obligations applicable to their profession, jurisdiction, role, practice setting, and relationship with the Client.',
      },
      {
        kind: 'clause',
        number: '6.4',
        text: 'The Practitioner may access, use, copy, export, or disclose Client information only to the extent reasonably necessary to prepare for or provide the Services requested by the Client or to satisfy a professional or legal obligation.',
      },
      {
        kind: 'clause',
        number: '6.5',
        text: 'Where a Client withdraws consent, Fem Entity may restrict or revoke the Practitioner’s Platform access to the affected information. The Practitioner must comply with any withdrawal communicated by Fem Entity except to the extent the Practitioner has an independent legal or professional authority to retain or use the information.',
      },
      {
        kind: 'clause',
        number: '6.6',
        text: 'Fem Entity uses cloud and technology service providers that may process information outside Canada. Fem Entity uses contractual or other measures designed to require a level of protection comparable to that required under applicable Canadian privacy law. The data processing agreement for Fem Entity’s primary hosting provider is available upon request.',
      },
    ],
  },
  {
    id: 'client-data-access',
    number: 7,
    title: 'Client Data Access',
    blocks: [
      {
        kind: 'clause',
        number: '7.1',
        text: 'Practitioners may access Client information made available through the Platform where the Client has deliberately selected the Practitioner or requested a referral and provided any consent required for the disclosure.',
      },
      {
        kind: 'clause',
        number: '7.2',
        text: 'A Client may choose to send one pre-appointment message to a selected Practitioner or clinic. The Practitioner may use that message only to prepare for and communicate about the requested appointment or to provide the professional services requested by the Client.',
      },
      {
        kind: 'clause',
        number: '7.3',
        text: 'Practitioners may access sensitive health and wellness information only where the Client has consented to its disclosure.',
      },
      {
        kind: 'clause',
        number: '7.4',
        text: 'Practitioners may transfer Client information to their own professional systems only where reasonably necessary to provide Services or meet professional or legal obligations.',
      },
      {
        kind: 'clause',
        number: '7.5',
        text: 'Practitioners are independently responsible for Client information within systems they select or control, including any additional notices or consents required by law or professional standards.',
      },
      {
        kind: 'clause',
        number: '7.6',
        text: 'Fem Entity may restrict, suspend, or revoke access where appropriate, including following withdrawal of consent, cancellation, termination, a privacy or security concern, or while Fem Entity reviews or investigates a matter.',
      },
      {
        kind: 'clause',
        number: '7.7',
        text: 'Practitioners must not retain or use Client information obtained through Fem Entity for purposes unrelated to providing Services or meeting applicable professional or legal obligations.',
      },
    ],
  },
  {
    id: 'video-sessions',
    number: 8,
    title: 'Video Sessions',
    blocks: [
      {
        kind: 'clause',
        number: '8.1',
        text: 'Practitioners are responsible for selecting, contracting for, configuring, and managing any virtual-care or video-conferencing system used to deliver their Services.',
      },
      {
        kind: 'clause',
        number: '8.2',
        text: 'A Practitioner may provide a meeting link to the Platform. Fem Entity stores and displays the link and basic scheduling metadata solely for appointment administration.',
      },
      {
        kind: 'clause',
        number: '8.3',
        text: 'Fem Entity does not host, control, monitor, record, or have access to virtual sessions conducted between Practitioners and Clients through practitioner-selected systems.',
      },
      {
        kind: 'clause',
        number: '8.4',
        text: 'The Practitioner must ensure that any selected system and its use comply with applicable privacy law, professional standards, confidentiality obligations, consent requirements, and the requirements of any applicable regulator or professional association.',
      },
      {
        kind: 'clause',
        number: '8.5',
        text: 'The Practitioner must provide Clients with any notices, instructions, or consents required for the selected system and the professional Services.',
      },
    ],
  },
  {
    id: 'fees-and-payment',
    number: 9,
    title: 'Fees and Payment',
    blocks: [
      {
        kind: 'clause',
        number: '9.1',
        text: 'The Platform fee structure shall be as communicated to the Practitioner at the time of onboarding and as updated from time to time. Fem Entity shall provide no less than thirty (30) days’ written notice of any change to the fee structure.',
      },
      {
        kind: 'clause',
        number: '9.2',
        text: 'All payments are processed through Stripe. The Practitioner acknowledges and agrees to Stripe’s terms of service as they apply to payment processing.',
      },
      {
        kind: 'clause',
        number: '9.3',
        text: 'The Practitioner is solely responsible for all tax reporting, remittance, and compliance obligations arising from income earned through the Platform.',
      },
    ],
  },
  {
    id: 'confidentiality',
    number: 10,
    title: 'Confidentiality',
    blocks: [
      {
        kind: 'clause',
        number: '10.1',
        text: 'The Practitioner must maintain the confidentiality of all Client information accessed through or in connection with the Platform, in accordance with applicable privacy legislation and professional standards.',
      },
      {
        kind: 'clause',
        number: '10.2',
        text: 'The Practitioner must not use data accessed through the Platform for marketing, solicitation, or any purpose other than the delivery of Services, unless the Practitioner has obtained separate, informed consent from the Client for such use.',
      },
      {
        kind: 'clause',
        number: '10.3',
        text: 'The obligations of confidentiality set out in this Section survive the termination or expiry of these Terms indefinitely.',
      },
    ],
  },
  {
    id: 'intellectual-property',
    number: 11,
    title: 'Intellectual Property',
    blocks: [
      {
        kind: 'clause',
        number: '11.1',
        text: 'The Platform, including all software, technology, branding, trade-marks, logos, designs, and proprietary content, is and shall remain the exclusive property of Fem Entity.',
      },
      {
        kind: 'clause',
        number: '11.2',
        text: 'The Practitioner retains ownership of their own professional materials, written content, biographical information, and intellectual property created independently of the Platform.',
      },
      {
        kind: 'clause',
        number: '11.3',
        text: 'The Practitioner grants Fem Entity a non-exclusive, royalty-free, revocable licence to display the Practitioner’s profile, biography, credentials, and likeness on the Platform for the purpose of facilitating the marketplace.',
      },
    ],
  },
  {
    id: 'compliance-and-professional-standards',
    number: 12,
    title: 'Compliance and Professional Standards',
    blocks: [
      {
        kind: 'clause',
        number: '12.1',
        text: 'The Practitioner must comply with all federal, provincial, and municipal laws, regulations, by-laws, professional standards, and codes of conduct applicable to their discipline and practice.',
      },
      {
        kind: 'clause',
        number: '12.2',
        text: 'The Practitioner must maintain, throughout their participation on the Platform, any professional liability insurance or equivalent professional protection required by applicable law, their regulator or professional association, and their applicable practice setting.',
      },
      {
        kind: 'clause',
        number: '12.3',
        text: 'Upon request, the Practitioner must provide Fem Entity with current evidence of that coverage or protection, including information reasonably necessary to confirm its scope, limits, and validity.',
      },
      {
        kind: 'clause',
        number: '12.4',
        text: 'The Practitioner must promptly notify Fem Entity of any cancellation, expiry, or material restriction affecting required coverage or protection.',
      },
      {
        kind: 'clause',
        number: '12.5',
        text: 'The Practitioner must promptly notify Fem Entity of any material regulatory action, restriction, suspension, formal disciplinary finding, or change to their registration or certification status.',
      },
      {
        kind: 'clause',
        number: '12.6',
        text: 'Fem Entity may conduct periodic or risk-based reverification, request updated documentation, and restrict or deactivate a profile while reviewing a discrepancy, safety concern, or change in eligibility.',
      },
    ],
  },
  {
    id: 'termination',
    number: 13,
    title: 'Termination',
    blocks: [
      {
        kind: 'sub',
        number: '13.1',
        title: 'Termination on Notice',
      },
      {
        kind: 'clause',
        number: '13.1.1',
        text: 'Either party may terminate these Terms by providing thirty (30) days’ written notice to the other party.',
      },
      {
        kind: 'sub',
        number: '13.2',
        title: 'Immediate Termination',
      },
      {
        kind: 'clause',
        number: '13.2.1',
        text: 'Fem Entity may terminate these Terms and suspend or revoke the Practitioner’s access to the Platform immediately, without notice, in any of the following circumstances:',
      },
      {
        kind: 'clause',
        number: '13.2.2',
        text: 'Material breach of any provision of these Terms;',
      },
      {
        kind: 'clause',
        number: '13.2.3',
        text: 'Regulatory action against the Practitioner, including suspension, revocation, or imposition of conditions on their registration;',
      },
      {
        kind: 'clause',
        number: '13.2.4',
        text: 'Reasonable grounds to believe that Client safety is at risk;',
      },
      {
        kind: 'clause',
        number: '13.2.5',
        text: 'Failure to maintain required credentials, certifications, or insurance.',
      },
      {
        kind: 'sub',
        number: '13.3',
        title: 'Effect of Termination',
      },
      {
        kind: 'clause',
        number: '13.3.1',
        text: 'Upon termination of these Terms for any reason:',
      },
      {
        kind: 'clause',
        number: '13.3.2',
        text: 'The Practitioner’s account access shall be revoked immediately or upon expiry of the notice period, as applicable;',
      },
      {
        kind: 'clause',
        number: '13.3.3',
        text: 'The Practitioner must manage all active bookings, including transitioning care to another provider or cancelling appointments with reasonable notice to affected Clients;',
      },
      {
        kind: 'clause',
        number: '13.3.4',
        text: 'Client data stored on the Platform shall no longer be accessible to the Practitioner;',
      },
      {
        kind: 'clause',
        number: '13.3.5',
        text: 'Any Client data lawfully exported by the Practitioner during the active term of these Terms becomes the Practitioner’s sole professional responsibility, subject to all applicable privacy legislation and professional record-keeping obligations;',
      },
      {
        kind: 'clause',
        number: '13.3.6',
        text: 'Payment for Services completed prior to termination shall be processed in accordance with the normal payment schedule.',
      },
    ],
  },
  {
    id: 'limitation-of-liability',
    number: 14,
    title: 'Limitation of Liability',
    blocks: [
      {
        kind: 'clause',
        number: '14.1',
        text: 'Fem Entity shall not be liable for: (i) the Practitioner’s professional services or the quality thereof; (ii) the outcomes of any practitioner-client relationship; (iii) any issues, interruptions, or failures related to third-party video conferencing platforms selected by the Practitioner; or (iv) any regulatory actions taken against the Practitioner by a regulatory body.',
      },
      {
        kind: 'clause',
        number: '14.2',
        text: 'The Platform is provided on an “as is” and “as available” basis. Fem Entity makes no warranty or representation that access to the Platform will be uninterrupted, timely, secure, or error-free.',
      },
      {
        kind: 'clause',
        number: '14.3',
        text: 'To the maximum extent permitted by applicable law, the aggregate liability of Fem Entity to the Practitioner under or in connection with these Terms, whether in contract, tort (including negligence), statute, or otherwise, shall not exceed the total fees paid by or on behalf of the Practitioner to Fem Entity in the twelve (12) months immediately preceding the event giving rise to the claim.',
      },
      {
        kind: 'clause',
        number: '14.4',
        text: 'In no event shall Fem Entity be liable for any consequential, indirect, incidental, special, exemplary, or punitive damages, including but not limited to loss of revenue, loss of profits, loss of clients, or loss of data, regardless of whether Fem Entity was advised of the possibility of such damages.',
      },
    ],
  },
  {
    id: 'indemnification',
    number: 15,
    title: 'Indemnification',
    blocks: [
      {
        kind: 'clause',
        number: '15.1',
        text: 'The Practitioner shall indemnify, defend, and hold harmless Fem Entity and its officers, directors, employees, agents, and affiliates from and against any and all claims, demands, losses, liabilities, damages, costs, and expenses (including reasonable legal fees) arising out of or in connection with:',
      },
      {
        kind: 'clause',
        number: '15.2',
        text: 'The Practitioner’s professional services, including any act, omission, error, or negligence in the delivery of such Services;',
      },
      {
        kind: 'clause',
        number: '15.3',
        text: 'Any breach by the Practitioner of any provision of these Terms;',
      },
      {
        kind: 'clause',
        number: '15.4',
        text: 'Any violation by the Practitioner of applicable law, regulation, or professional standard;',
      },
      {
        kind: 'clause',
        number: '15.5',
        text: 'Any unauthorized access to, use of, disclosure of, or failure to safeguard Client data by the Practitioner.',
      },
    ],
  },
  {
    id: 'dispute-resolution',
    number: 16,
    title: 'Dispute Resolution',
    blocks: [
      {
        kind: 'clause',
        number: '16.1',
        text: 'These Terms shall be governed by and construed in accordance with the laws of the Province of Nova Scotia and the federal laws of Canada applicable therein, without regard to conflict of laws principles.',
      },
      {
        kind: 'clause',
        number: '16.2',
        text: 'The courts of the Province of Nova Scotia shall have exclusive jurisdiction over any dispute arising out of or in connection with these Terms.',
      },
      {
        kind: 'clause',
        number: '16.3',
        text: 'Prior to commencing any legal proceeding, the parties agree to attempt to resolve any dispute through good faith negotiation for a period of not less than thirty (30) days following written notice of the dispute.',
      },
    ],
  },
  {
    id: 'general-provisions',
    number: 17,
    title: 'General Provisions',
    blocks: [
      {
        kind: 'clause',
        number: '17.1',
        lead: 'Entire Agreement.',
        text: 'These Terms, together with any policies or guidelines published on the Platform and incorporated by reference, constitute the entire agreement between the parties with respect to the subject matter hereof and supersede all prior agreements, representations, and understandings.',
      },
      {
        kind: 'clause',
        number: '17.2',
        lead: 'Severability.',
        text: 'If any provision of these Terms is held to be invalid, illegal, or unenforceable, such provision shall be severed and the remaining provisions shall continue in full force and effect.',
      },
      {
        kind: 'clause',
        number: '17.3',
        lead: 'Waiver.',
        text: 'No failure or delay by either party in exercising any right or remedy under these Terms shall constitute a waiver of that right or remedy. Any waiver must be in writing and signed by the waiving party.',
      },
      {
        kind: 'clause',
        number: '17.4',
        lead: 'Assignment.',
        text: 'The Practitioner may not assign or transfer these Terms or any rights hereunder without the prior written consent of Fem Entity. Fem Entity may assign these Terms without the Practitioner’s consent in connection with a merger, acquisition, or sale of substantially all of its assets.',
      },
      {
        kind: 'clause',
        number: '17.5',
        lead: 'Notices.',
        text: 'All notices under these Terms shall be in writing and shall be sent to the email address associated with the Practitioner’s account or to hello@fementity.ca for notices to Fem Entity.',
      },
      {
        kind: 'clause',
        number: '17.6',
        lead: 'Amendment.',
        text: 'Fem Entity may amend these Terms from time to time by providing no less than thirty (30) days’ written notice to the Practitioner. Continued use of the Platform following such notice constitutes acceptance of the amended Terms.',
      },
    ],
  },
  {
    id: 'contact',
    number: 18,
    title: 'Contact',
    blocks: [
      {
        kind: 'clause',
        number: '18.1',
        text: 'For questions, concerns, or notices regarding these Terms, please contact:',
      },
      {
        kind: 'contact',
        title: 'Fem Entity Inc.',
        lines: [
          {
            value: 'Nova Scotia, Canada',
          },
          {
            label: 'Privacy Officer',
            value: 'Mukisa Kakembo, CEO',
          },
          {
            label: 'Email',
            value: 'hello@fementity.ca',
            href: 'mailto:hello@fementity.ca',
          },
        ],
      },
      {
        kind: 'clause',
        number: '18.6',
        text: 'By creating an account on the Platform, the Practitioner acknowledges that they have read, understood, and agree to be bound by these Terms of Service.',
      },
    ],
  },
]
